import React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import SolidGauge from 'highcharts/modules/solid-gauge';

HighchartsMore(Highcharts);
SolidGauge(Highcharts);

export type MvxGaugeBand = { color: string; from: number; to: number };

/**
 * The tick lines, in the bar graphs' own grid-line color, so a mark means the
 * same thing and looks the same on both shapes.
 */
const TICK_COLOR = '#CDD7EA';

/* The ring, as Highcharts takes it: the pane background and every plot band
 * run from RING_INNER% to RING_OUTER% of the pane radius. */
const RING_INNER = 60;
const RING_OUTER = 100;
/* The pane is sized 100%, so its radius is half the 260px chart box. */
const PANE_RADIUS = 130;
/**
 * How deep a tick cuts. The FULL thickness of the colored ring (Maryann,
 * 2026-10-16: "put the tick lines all the way through the gauge, not just
 * stopping at the number"), which is what the bars do: there a grid line
 * crosses its whole band rather than nicking the edge of it.
 *
 * Derived rather than written as 52, so the lines still span the ring if the
 * radii above ever move. The numbers sit ON the ring at distance -15 and
 * carry a 2px white outline, so each line reads as passing behind its own
 * number rather than striking through it.
 */
const TICK_LENGTH = (PANE_RADIUS * (RING_OUTER - RING_INNER)) / 100;

type Props = {
  /** MVX score (0–100) — the needle position. */
  value: number;
  /** Level word shown in blue, e.g. "High" / "Elevated" / "Borderline" / "Optimal". */
  category: string;
  /** Always-there black line above the level, e.g. "Metabolic Vulnerability is". */
  text: string;
  /** Zone bands on the 0–100 scale (green → red around the decision limit). */
  plotBands: MvxGaugeBand[];
  /** Score values to label on the arc (e.g. the zone boundaries + endpoints). */
  ticks?: number[];
  /** Top of the gauge scale. Defaults to 100 — but the MVX score can exceed 100. */
  max?: number;
  chartContainerId?: string;
};

/**
 * MVX hero gauge — the exact Highcharts solid-gauge treatment the app uses for
 * every Insights gauge (mirrors LabsmartsGuage), so it matches the Glucose gauge
 * look: a colored 0–100 arc + needle with "{text}" (black) over
 * "{category} ({value})" (blue) beneath it. Only the plot bands (MVX zones) and
 * the no-percent value differ from the shared gauge.
 */
export class MvxScoreGauge extends React.Component<Props> {
  createOptions(): Highcharts.Options {
    const { value, category, text, plotBands, ticks, max } = this.props;
    const showTicks = !!(ticks && ticks.length);
    const scaleMax = max && max > 0 ? max : 100;
    return {
      title: { text: '' },
      chart: {
        type: 'solidgauge',
        height: 200,
        width: 260,
        // Exact same footprint as the Glucose gauge (LabsmartsGuage), so the arc
        // is the same size and sits in the same spot.
        margin: [-20, 0, -40, 0],
        backgroundColor: 'transparent',
      },
      pane: {
        center: ['50%', '60%'],
        size: '100%',
        startAngle: -90,
        endAngle: 90,
        background: [{ innerRadius: `${RING_INNER}%`, outerRadius: `${RING_OUTER}%`, shape: 'arc' }],
      },
      yAxis: {
        min: 0,
        max: scaleMax,
        lineWidth: 0,
        // A SHORT DASH ON EVERY LABELLED NUMBER (Maryann, 2026-10-16: "the
        // numbers look strange floating there without being on tick lines").
        //
        // The other gauges in the app draw none, and that was right while
        // their numbers were only zone boundaries, where the color changes
        // under them and says where they are. This one now carries a RULER as
        // well — the same marks as the bar below it — and a ruler mark lands
        // mid-band with nothing to tie it to, so it needs its own line.
        //
        // 'inside' is not optional: the pane fills the chart box, so a tick
        // drawn outward is clipped. It runs from the ring's outer edge clean
        // through to the inner one — see TICK_LENGTH. Minor ticks stay off,
        // and tickWidth alone does not silence them, since every mark here is
        // a deliberate one.
        tickWidth: 1,
        tickLength: TICK_LENGTH,
        tickPosition: 'inside',
        tickColor: TICK_COLOR,
        minorTickWidth: 0,
        minorTickLength: 0,
        // Numbers sit just inside the colored ring, so they never widen the
        // 260px chart the way outside labels (0 / 100 at the arc ends) would.
        tickPositions: showTicks ? ticks : [],
        labels: {
          enabled: showTicks,
          // Just inside the ring — the arc fills the chart (glucose footprint), so
          // there's no room outside it within the panel width.
          distance: -15,
          style: { fontSize: '10.5px', color: '#3a3a3a', fontWeight: '600', textOutline: '2px #ffffff' },
        },
        plotBands: plotBands.map(b => ({
          color: b.color,
          from: b.from,
          to: b.to,
          innerRadius: `${RING_INNER}%`,
          outerRadius: `${RING_OUTER}%`,
        })),
      },
      series: [
        {
          type: 'gauge',
          data: [value],
          dataLabels: {
            useHTML: true,
            format: `<div class="anemiaStatusGuageLabelContainer">${text}<br/>
                        <div class="anemiaStatusGuageLabel">
                            ${category} (${value})
                        </div>
                     </div>`,
            borderWidth: 0,
          },
        } as Highcharts.SeriesGaugeOptions,
      ],
      credits: { enabled: false },
      exporting: { enabled: false },
      tooltip: { enabled: false },
    };
  }

  render() {
    return (
      <HighchartsReact
        highcharts={Highcharts}
        options={this.createOptions()}
        containerProps={{ id: this.props.chartContainerId }}
      />
    );
  }
}

export default MvxScoreGauge;
